import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { FileDown, QrCode, ShieldCheck, Wrench } from "lucide-react";

import { BrandMark } from "@/components/BrandMark";
import { StatusBadge } from "@/components/StatusBadge";
import { fetchSurfaceByToken } from "@/lib/api";
import { apiBaseUrl } from "@/lib/config";
import { createSampleRecord, SAMPLE_TOKEN } from "@/lib/sample";
import { LOCALES } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type { CertificateStatus, Locale, SurfaceRecord } from "@/lib/types";

const UI = {
  it: {
    certified: "Superficie VERSUS certificata",
    project: "Progetto",
    client: "Committente",
    site: "Cantiere",
    surface: "Superficie",
    area: "Area coperta",
    type: "Tipologia",
    system: "Sistema VERSUS",
    finish: "Finitura",
    colour: "Colore",
    applicator: "Applicatore autorizzato",
    academy: "Livello Academy",
    patentino: "Patentino n.",
    installed: "Data posa",
    collaudo: "Collaudo",
    warranty: "Copertura garanzia",
    years: "anni",
    expires: "Scadenza",
    maintenance: "Manutenzione",
    lastCheck: "Ultimo controllo",
    nextCheck: "Prossimo controllo",
    download: "Scarica il certificato",
    report: "Segnala un problema",
    reportHint:
      "Apri una richiesta di intervento su questa superficie. Il team VERSUS riceverà l'ID e il tuo contatto.",
    name: "Nome",
    email: "Email",
    describe: "Descrivi il problema",
    send: "Invia segnalazione",
    sending: "Invio…",
    sent: "Segnalazione inviata. Grazie.",
    error: "Invio non riuscito. Riprova.",
    demo:
      "Anteprima demo — dati di esempio. Il record reale arriva dall'API.",
    statusLabel: { valid: "Valido", revoked: "Revocato", superseded: "Sostituito" },
  },
  en: {
    certified: "VERSUS certified surface",
    project: "Project",
    client: "Client",
    site: "Site",
    surface: "Surface",
    area: "Covered area",
    type: "Type",
    system: "VERSUS system",
    finish: "Finish",
    colour: "Colour",
    applicator: "Authorised applicator",
    academy: "Academy level",
    patentino: "Credential no.",
    installed: "Installation",
    collaudo: "Final inspection",
    warranty: "Warranty coverage",
    years: "years",
    expires: "Expires",
    maintenance: "Maintenance",
    lastCheck: "Last check",
    nextCheck: "Next check",
    download: "Download certificate",
    report: "Report an issue",
    reportHint:
      "Raise an intervention request for this surface. The VERSUS team receives the ID and your contact.",
    name: "Name",
    email: "Email",
    describe: "Describe the issue",
    send: "Send report",
    sending: "Sending…",
    sent: "Report sent. Thank you.",
    error: "Could not send. Please try again.",
    demo: "Demo preview — sample data. The live record comes from the API.",
    statusLabel: { valid: "Valid", revoked: "Revoked", superseded: "Superseded" },
  },
  es: {
    certified: "Superficie VERSUS certificada",
    project: "Proyecto",
    client: "Cliente",
    site: "Obra",
    surface: "Superficie",
    area: "Superficie cubierta",
    type: "Tipología",
    system: "Sistema VERSUS",
    finish: "Acabado",
    colour: "Color",
    applicator: "Aplicador autorizado",
    academy: "Nivel Academy",
    patentino: "Credencial n.º",
    installed: "Fecha de instalación",
    collaudo: "Inspección final",
    warranty: "Cobertura de garantía",
    years: "años",
    expires: "Vencimiento",
    maintenance: "Mantenimiento",
    lastCheck: "Último control",
    nextCheck: "Próximo control",
    download: "Descargar el certificado",
    report: "Señalar un problema",
    reportHint:
      "Abre una solicitud de intervención sobre esta superficie. El equipo VERSUS recibirá el ID y tu contacto.",
    name: "Nombre",
    email: "Email",
    describe: "Describe el problema",
    send: "Enviar aviso",
    sending: "Enviando…",
    sent: "Aviso enviado. Gracias.",
    error: "No se pudo enviar. Inténtalo de nuevo.",
    demo: "Vista previa de demostración — datos de ejemplo. El registro real llega desde la API.",
    statusLabel: { valid: "Válido", revoked: "Revocado", superseded: "Sustituido" },
  },
} as const;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-5 border-b border-white/10 py-2.5 last:border-0">
      <span className="text-[0.6rem] uppercase tracking-[0.18em] text-ice/65">
        {label}
      </span>
      <span className="text-right text-[0.86rem] leading-snug">{children}</span>
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="glass-panel-strong mt-4 p-4 sm:p-5">
      <h2 className="flex items-center gap-2 text-[0.62rem] font-semibold uppercase tracking-[0.22em] text-goldbright">
        {icon}
        {title}
      </h2>
      <div className="mt-2.5">{children}</div>
    </section>
  );
}

function ReportForm({ token, locale }: { token: string; locale: Locale }) {
  const t = UI[locale];
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setState("sending");
    try {
      const res = await fetch(
        `${apiBaseUrl}/public/verify/${encodeURIComponent(token)}/report`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            contactName: form.get("name"),
            contactEmail: form.get("email"),
            description: form.get("description"),
          }),
        },
      );
      if (!res.ok) throw new Error(String(res.status));
      setState("sent");
    } catch {
      setState("error");
    }
  }

  if (state === "sent") {
    return <p className="mt-3 text-sm text-green">{t.sent}</p>;
  }

  return (
    <form className="mt-3 grid gap-3" onSubmit={onSubmit}>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="field-label">{t.name}</span>
          <input name="name" required className="field" />
        </label>
        <label className="block">
          <span className="field-label">{t.email}</span>
          <input name="email" type="email" required className="field" />
        </label>
      </div>
      <label className="block">
        <span className="field-label">{t.describe}</span>
        <textarea name="description" required rows={3} className="field resize-y" />
      </label>
      <button
        type="submit"
        disabled={state === "sending"}
        className="mt-1 inline-flex w-fit items-center gap-2 rounded-full bg-magenta px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-paper transition hover:brightness-110 disabled:opacity-60"
      >
        <Wrench className="h-3.5 w-3.5" />
        {state === "sending" ? t.sending : t.send}
      </button>
      {state === "error" && <p className="text-xs text-pink">{t.error}</p>}
    </form>
  );
}

export default function VerifyPage() {
  const { token = SAMPLE_TOKEN } = useParams();
  const [locale, setLocale] = useState<Locale>("it");
  const [remote, setRemote] = useState<SurfaceRecord | null>(null);
  const [source, setSource] = useState<"loading" | "api" | "sample">("loading");

  useEffect(() => {
    const ctrl = new AbortController();
    setSource("loading");
    fetchSurfaceByToken(token, ctrl.signal)
      .then((r) => {
        setRemote(r);
        setSource("api");
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setSource("sample");
      });
    return () => ctrl.abort();
  }, [token]);

  const sample = useMemo(() => createSampleRecord(locale), [locale]);
  const record = remote ?? sample;
  const t = UI[locale];

  return (
    <div className="app-surface min-h-screen px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-2xl">
        <header className="flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <BrandMark className="h-11 w-11" />
            <div>
              <div className="brand-wordmark text-[0.7rem] text-paper">VERSUS</div>
              <div className="text-[0.55rem] uppercase tracking-[0.3em] text-ice/60">
                Concept
              </div>
            </div>
          </Link>
          <div className="flex gap-1 rounded-full border border-white/15 p-0.5">
            {LOCALES.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLocale(l)}
                className={cn(
                  "rounded-full px-3 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.16em] transition",
                  locale === l ? "bg-white/15 text-paper" : "text-ice/60",
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </header>

        <div className="mt-8 text-center">
          <div className="flex justify-center">
            <BrandMark className="h-16 w-16" />
          </div>
          <div className="brand-kicker mt-3">{t.certified}</div>
          <div className="num mt-1 font-serif text-3xl tracking-[0.06em] text-pink sm:text-4xl">
            {record.serial}
          </div>
          <div className="mt-3 flex justify-center">
            <StatusBadge
              status={record.status as CertificateStatus}
              label={t.statusLabel[record.status]}
            />
          </div>
        </div>

        <Section title={t.project} icon={<ShieldCheck className="h-3.5 w-3.5" />}>
          {!record.project.hideName && (
            <Row label={t.project}>{record.project.name}</Row>
          )}
          <Row label={t.client}>{record.project.client}</Row>
          <Row label={t.site}>
            {record.project.siteAddress}
            {record.project.city ? ` · ${record.project.city}` : ""}
          </Row>
        </Section>

        <Section title={t.surface} icon={<QrCode className="h-3.5 w-3.5" />}>
          <Row label={t.surface}>{record.surface.name}</Row>
          <Row label={t.area}>
            <span className="num">{record.surface.m2}</span> m²
          </Row>
          <Row label={t.type}>
            {record.surface.typeLabel}
            {record.surface.location ? ` · ${record.surface.location}` : ""}
          </Row>
          <Row label={t.system}>{record.surface.systemLabel}</Row>
          <Row label={t.finish}>{record.surface.finishLabel}</Row>
          <Row label={t.colour}>
            <span className="inline-flex items-center justify-end gap-2">
              {record.surface.colorSwatch && (
                <span
                  className="inline-block h-4 w-4 rounded border border-white/40"
                  style={{ background: record.surface.colorSwatch }}
                />
              )}
              {record.surface.colorRef ?? "—"}
            </span>
          </Row>
        </Section>

        <Section title={t.applicator} icon={<ShieldCheck className="h-3.5 w-3.5" />}>
          <Row label={t.applicator}>{record.applicator.companyName}</Row>
          <Row label={t.academy}>
            <span className="text-goldbright">{record.applicator.academyLabel}</span>
          </Row>
          <Row label={t.patentino}>
            <span className="num">{record.applicator.patentinoNumber}</span>
          </Row>
          <Row label={t.installed}>
            <span className="num">{record.dates.installed}</span>
          </Row>
          <Row label={t.collaudo}>
            <span className="num">{record.dates.collaudo}</span>
          </Row>
        </Section>

        <Section title={t.warranty} icon={<ShieldCheck className="h-3.5 w-3.5" />}>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 text-[0.6rem] uppercase tracking-[0.16em] text-ice/60">
            <span />
            <span>{t.years}</span>
            <span>{t.expires}</span>
          </div>
          {record.warranty.map((w) => (
            <div
              key={w.kind}
              className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-4 border-b border-white/10 py-2.5 last:border-0"
            >
              <span className="text-[0.86rem]">{w.label}</span>
              <span className="num text-[0.86rem] font-semibold text-goldbright">
                {w.years}
              </span>
              <span className="num text-[0.86rem] text-ice/75">{w.expiresAt}</span>
            </div>
          ))}
        </Section>

        {record.maintenance && (
          <Section title={t.maintenance} icon={<ShieldCheck className="h-3.5 w-3.5" />}>
            <Row label={t.lastCheck}>
              <span className="num">{record.maintenance.last ?? "—"}</span>
            </Row>
            <Row label={t.nextCheck}>
              <span className="num">{record.maintenance.next ?? "—"}</span>
              {record.maintenance.nextKind && (
                <span className="ml-2 text-ice/50">
                  {record.maintenance.nextKind === "top_coat_renewal"
                    ? locale === "it"
                      ? "rinnovo top coat"
                      : "top-coat renewal"
                    : locale === "it"
                      ? "controllo annuale"
                      : "annual check"}
                </span>
              )}
            </Row>
          </Section>
        )}

        <Section title={t.report} icon={<Wrench className="h-3.5 w-3.5" />}>
          <p className="text-xs leading-relaxed text-ice/70">{t.reportHint}</p>
          <ReportForm token={token} locale={locale} />
        </Section>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <a
            href={`${apiBaseUrl}/public/verify/${encodeURIComponent(token)}/certificate.pdf`}
            target="_blank"
            rel="noreferrer"
            className="glass-panel inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-paper transition hover:border-pink/50"
          >
            <FileDown className="h-3.5 w-3.5" />
            {t.download}
          </a>
          {source !== "api" && (
            <p className="text-[0.65rem] leading-relaxed text-ice/50">{t.demo}</p>
          )}
        </div>
      </div>
    </div>
  );
}
